import { del, get, set } from 'idb-keyval';

import { isProd } from '~/client/config/appConfig';
import { trackError } from '~/client/lib/appInsights';

import { PersistedContentType, PersisterStoreName } from './types';

/**
 * Bump whenever a release makes previously-persisted store content
 * incompatible with the current code or the server's hashing. Clients on an
 * older marker purge their persisted stores once and re-sync cleanly, instead
 * of re-downloading the full store on every connect.
 */
const PERSISTENCE_VERSION = 2;
const PERSISTENCE_VERSION_KEY = 'persistence-version';
const UNDEFINED = '\uFFFC';

export const onIgnoredError = (tableName: PersisterStoreName) =>
  !isProd
    ? (error: unknown) => {
        console.error(`[${tableName}] Ignored persister error: `, error);
        trackError(error, { tableName, clientPersisterError: true });
      }
    : undefined;

export const jsonStringWithUndefined = (obj: unknown): string =>
  JSON.stringify(obj, (_key, value) => (value === undefined ? UNDEFINED : value));

export const jsonParseWithUndefined = (str: string): PersistedContentType =>
  JSON.parse(str, (_key, value) => (value === UNDEFINED ? undefined : value));

export const clearOutdatedPersistedData = async () => {
  try {
    const persistedVersion = await get<number>(PERSISTENCE_VERSION_KEY);

    if (persistedVersion === PERSISTENCE_VERSION) {
      return;
    }

    // Only clears store content — crypto params (salt/IV) are left intact so
    // the user store re-encrypts under the same key after re-syncing. Any
    // unsynced user store changes are deliberately discarded (users are asked
    // to sync before an incompatible release, and the server-side user stores
    // are cleared alongside — see ClearUserStoresCommand.ts)
    await Promise.all([
      del('tinybase-global' satisfies PersisterStoreName),
      del('tinybase-user' satisfies PersisterStoreName),
    ]);

    await set(PERSISTENCE_VERSION_KEY, PERSISTENCE_VERSION);
  } catch (error) {
    console.error('Failed to clear outdated persisted store data', error);
    trackError(error, { clearOutdatedPersistedDataError: true });
  }
};

export const deletePersistedData = async (storeName: PersisterStoreName) => {
  await del(storeName);
  console.log(`Deleted persisted data for ${storeName}`);
};
